import React from 'react';

import {
  mapDispatchToProps,
  mapStateToProps,
  type FixedEditingAreaProps,
} from './fixedEditingAreaMapper';
import {
  TABLET_WIDTH,
  DESKTOP_WIDTH,
  WIDE_DESKTOP_WIDTH,
  MOBILE_CANVAS_SIZE,
  COMMONLY_SMALL_TABLET,
  DESKTOP_CANVAS_SIZE,
  COMMONLY_LARGE_LAPTOP,
  LARGE_SCREEN_SIZE,
} from '#packages/preview';
import * as util from '#packages/util';
import styles from './fixedEditingArea.scss';
import constants from '#packages/constants';
import { translate } from '#packages/i18n';

import { InfoIcon, Text } from '@wix/wix-base-ui';
import MobileFrame from '../../mobileFrame/mobileFrame';
import { PreviewFrame } from '../../previewFrame/previewFrame';
import { StageContainer } from '../../stage/stageContainer';
import OpenedPanels from '../../panels/openedPanels';
import Portal from '../../portal/portal';
import { mobileWizard as MobileWizard } from '#packages/mobileEditor';

const FixedEditingArea: React.FC<FixedEditingAreaProps> = (props) => {
  const wrapperRef = React.useRef<HTMLDivElement>();
  const editingAreaRef = React.useRef<HTMLDivElement>();
  const [scrollLeftPos, setScrollLeftPos] = React.useState<number>(0);

  const getEditingAreaRect = () => {
    const { top, left, height, width, right, bottom } =
      editingAreaRef.current.getBoundingClientRect();
    return { top, left, height, width, right, bottom };
  };

  const updatePositionsInState = () => {
    if (editingAreaRef.current?.getBoundingClientRect) {
      props.refreshEditingAreaPosition(getEditingAreaRect());
    }
  };
  if (!props.performingMouseMoveAction) {
    updatePositionsInState();
  }

  const handleWheel = (event: React.WheelEvent<HTMLDivElement>): void => {
    const { ctrlKey } = event;
    if (ctrlKey) {
      event.preventDefault();
      props.zoomByMouseWheel(event);
    }
  };

  const getPreviewWidthInfoText = () => {
    const getWidthDescriptionKey = () => {
      switch (props.previewWidth) {
        case MOBILE_CANVAS_SIZE:
          return 'preview_width_label_suffix_mobile';
        case COMMONLY_SMALL_TABLET:
          return 'preview_width_label_suffix_tablet';
        case TABLET_WIDTH:
          return 'preview_width_label_suffix_tablet_laptop';
        case DESKTOP_CANVAS_SIZE:
          return 'preview_width_label_suffix_editing_size';
        case DESKTOP_WIDTH:
          return 'preview_width_label_suffix_laptop';
        case COMMONLY_LARGE_LAPTOP:
          return 'preview_width_label_suffix_large_laptop';
        case WIDE_DESKTOP_WIDTH:
          return 'preview_width_label_suffix_desktop';
        case LARGE_SCREEN_SIZE:
          return 'preview_width_label_suffix_wide_desktop';
        default:
          return null;
      }
    };

    const siteZoom = props.siteScale * 100;

    return translate(getWidthDescriptionKey(), {
      width: props.previewWidth,
      zoom_percent: siteZoom,
    });
  };

  const getPreviewWidthInfoSkin = () => {
    if (
      props.previewWidth === MOBILE_CANVAS_SIZE ||
      props.previewWidth === DESKTOP_CANVAS_SIZE
    ) {
      return 'link';
    }

    return 'placeholder';
  };

  const getEditingAreaClassName = () => {
    return util.cx(styles.fixedEditingArea, {
      [styles.mobileWizardEditingArea]: props.shouldRenderMobileWizard,
      [styles.newMobileEditorLayoutWithStageBanner]:
        props.isStageBannerEnabled && props.isMobileEditor,
      [styles.fullWidth]: props.isFullScreenPreview,
    });
  };

  const getEditingAreaTopBottom = () => {
    const editingAreaMargins = { top: 0, bottom: 0 };
    if (
      util.sections.isSectionsEnabled() &&
      props.isZoomOut &&
      !props.isMobileEditor
    ) {
      editingAreaMargins.top = 18;
      editingAreaMargins.bottom = 18;
    }
    if (props.shouldRenderFixedStageTopGap) {
      editingAreaMargins.top = constants.UI.FIXED_STAGE_MARGIN_TOP;
    }
    if (props.isStageBannerEnabled) {
      editingAreaMargins.top += constants.UI.STAGE_BANNER_HEIGHT;
    }
    return editingAreaMargins;
  };

  const getEditingAreaStyle = () => {
    const { shouldCutEditingArea, fixedStageWidth } = props;

    const stageRightMargin = shouldCutEditingArea
      ? constants.UI.EDITING_AREA_RIGHT_MARGIN
      : 0;

    const { x, width } =
      props.leftBarAreaRef.current?.getBoundingClientRect() || {
        x: 0,
        width: 0,
      };

    const { top, bottom } = getEditingAreaTopBottom();

    const scrollbarWidth = util.uiUtils.getScrollbarWidth();

    const fixedStageMarginsSum =
      (props.editingAreaWidth ||
        (editingAreaRef.current && getEditingAreaRect().width)) -
      fixedStageWidth;

    const fixedStageMarginWithoutConstrains = fixedStageMarginsSum / 2;

    const rightMarginFixedStage = Math.max(
      fixedStageMarginWithoutConstrains,
      stageRightMargin,
    );

    const leftMarginFixedStage = fixedStageMarginsSum - rightMarginFixedStage;

    const propertiesForFixedStage = {
      '--stage-left': `${x + width}px`,
      '--stage-top': `${top}px`,
      '--stage-bottom': `${bottom}px`,
      '--stage-scrollbar-width': `${scrollbarWidth}px`,
      '--stage-fixed-width': `${fixedStageWidth}px`,
      '--site-scale': props.siteScale,
      '--stage-height': props.isMobileEditor ? '100%' : props.stageHeight,
      '--stage-right-margin': `${rightMarginFixedStage}px`,
      '--stage-left-margin': `${leftMarginFixedStage}px`,
      '--horizontal-ruler-right': `${
        (rightMarginFixedStage - stageRightMargin) * -1
      }px`,
      '--horizontal-ruler-top': `${top * -1}px`,
      '--preview-horizontal-margin': 0,
      '--preview-width': `${props.previewWidth}px`,
    };

    return {
      ...propertiesForFixedStage,
    } as React.CSSProperties;
  };

  const onScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (props.isPreviewMode) {
      return;
    }
    if (event.target !== wrapperRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    const target = event.target as HTMLDivElement;
    setScrollLeftPos(target.scrollLeft);
    const previewNode = window.__previewFrameData.mountAndGetFrame();
    previewNode.style.left = `${
      parseInt(previewNode.style.left, 10) + scrollLeftPos - target.scrollLeft
    }px`;
    props.registerNewScrollPosition(event);
  };

  return (
    <div
      className={util.cx(styles.fixedEditorLayoutEditingArea, styles.fixed, {
        [styles.truncated]: props.shouldCutEditingArea,
      })}
      id={constants.ROOT_COMPS.SELECTOR_ID.FIXED_SCROLLABLE_EDITOR_LAYOUT_AREA}
      style={getEditingAreaStyle()}
      onScroll={onScroll}
      onWheel={handleWheel}
      ref={wrapperRef}
    >
      <div
        ref={editingAreaRef}
        id={constants.ROOT_COMPS.SELECTOR_ID.EDITING_AREA}
        className={getEditingAreaClassName()}
        data-hook={
          props.isMobileEditor ? 'editing-area-mobile' : 'editing-area'
        }
      >
        {props.isPreviewMode && !props.isFullScreenPreview && (
          <div
            className={styles.previewWidthInfo}
            data-hook="preview-width-info"
          >
            <Text
              size="tiny"
              shouldTranslate={false}
              skin={getPreviewWidthInfoSkin()}
            >
              {getPreviewWidthInfoText()}
            </Text>
            {props.previewWidth === LARGE_SCREEN_SIZE && (
              <InfoIcon
                text="preview_width_label_suffix_wide_desktop_tooltip"
                small={true}
              />
            )}
          </div>
        )}

        {props.isStageBannerEnabled && (
          <props.contributedStageBannerComponent />
        )}

        <div
          className={styles.fixedPreviewFrameCover}
          style={{
            width: props.isPreviewMode
              ? props.previewWidth * props.siteScale
              : props.fixedStageWidth,
            height:
              props.isPreviewMode && !props.isFullScreenPreview
                ? 'calc(100% - 42px)'
                : '100%',
          }}
        >
          {props.isMobileEditor && <MobileFrame />}

          <PreviewFrame
            styleForPushedStageAndPreviewFrame={
              props.styleForPushedStageAndPreviewFrame
            }
            truncated={true}
            fixed={true}
            scrollboxRef={wrapperRef}
            mobile={props.isMobileEditor}
            mobileWizard={
              props.mobileWizardOpeningState ===
              constants.MOBILE.MOBILE_WIZARD.OPENING_STATE.IN_PROGRESS
            }
            shouldAnimatePreviewWidth={props.shouldAnimatePreviewWidth}
          />
        </div>

        <Portal />

        {props.shouldRenderMobileWizard && <MobileWizard />}
      </div>
      {!props.isPreviewMode && !props.shouldRenderMobileWizard && (
        <StageContainer
          styleForPushedStageAndPreviewFrame={
            props.styleForPushedStageAndPreviewFrame
          }
        />
      )}
      <OpenedPanels
        helpPanelProps={props.helpPanelProps}
        selectedComponents={props.selectedComponents}
        isMobileEditor={props.isMobileEditor}
      />
    </div>
  );
};

export default util.hoc.connect(
  util.hoc.STORES.EDITOR_API,
  mapStateToProps,
  mapDispatchToProps,
)(FixedEditingArea);
